import { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, Divider, MenuItem, CircularProgress,
} from '@mui/material';
import { DeliveryPriceResult, DateOnly, RetailerHeaderModel, UserFranchiseList } from '../../../app/services/api/ofdClient';
import { client, DeliveryMethod } from '../../../app/services/api/orderManagementClient';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { UserContext } from '../../../components/shared/useUser';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';

interface Props {
  open: boolean;
  onClose: () => void;
}

const PriceCalculator: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation('deliveryPricing');
  const { selectedFranchise, selectedStore } = useContext(UserContext);
  const { deliveryPricingClient, getRetailersList } = useNswagClient();
  const getRetailersListRef = useRef(getRetailersList);

  useEffect(() => {
    getRetailersListRef.current = getRetailersList;
  }, [getRetailersList]);

  const [shippingMethodId, setShippingMethodId] = useState('');
  const [shippingMethods, setShippingMethods] = useState<DeliveryMethod[]>([]);
  const [retailers, setRetailers] = useState<RetailerHeaderModel[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [retailerId, setRetailerId] = useState('');
  const [wholePallets, setWholePallets] = useState('1');
  const [lastPalletFillPercent, setLastPalletFillPercent] = useState('100');
  const [deliveryDate, setDeliveryDate] = useState(new Date().toISOString().split('T')[0]);
  const [baseCharge, setBaseCharge] = useState('');

  const [result, setResult] = useState<DeliveryPriceResult | null>(null);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState('');
  const today = useMemo(() => new Date().toISOString().split('T')[0], []);

  useEffect(() => {
    if (!open) return;

    const currentFranchiseName = selectedFranchise?.franchiseName ?? '';
    const userFranchiseList: UserFranchiseList = {
      // Scope to the current store's franchise only. The API's tenant-isolation check (403) is enforced
      // against the current store's ExternalCustomerTypeId, so offering retailers from other franchises
      // the user happens to have access to would let them pick one that fails on calculate.
      franchiseNames: currentFranchiseName ? [currentFranchiseName] : [],
      otherRegions: false,
      currentFranchiseName,
    };
    const storeNumber = selectedStore?.storeNumber?.toString();

    setLoadingOptions(true);
    Promise.all([
      client.getOnboardingDeliveryMethods(storeNumber).then(result => result?.data?.ShippingMethodList ?? []),
      getRetailersListRef.current(userFranchiseList),
    ])
      .then(([methods, retailerResult]) => {
        setShippingMethods(methods ?? []);
        setRetailers(retailerResult.retailerList ?? []);
      })
      .catch(() => {
        setShippingMethods([]);
        setRetailers([]);
      })
      .finally(() => setLoadingOptions(false));
  }, [open, selectedFranchise, selectedStore?.storeNumber]);

  const sortedRetailers = useMemo(
    () => [...retailers].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
    [retailers],
  );

  const handleCalculate = async () => {
    setError('');
    setResult(null);

    if (!shippingMethodId) { setError(t('calculatorForm.shippingRequired', 'Shipping method is required')); return; }
    const wholePalletsNumber = parseFloat(wholePallets);
    if (!Number.isFinite(wholePalletsNumber) || wholePalletsNumber < 0) {
      setError(t('calculatorForm.wholePalletsInvalid', 'Whole delivery units must be 0 or greater'));
      return;
    }

    const fillPercent = parseFloat(lastPalletFillPercent);
    if (!Number.isFinite(fillPercent) || fillPercent < 0 || fillPercent > 100) {
      setError(t('calculatorForm.lastPalletFillPercentInvalid', 'Last delivery unit fill percent must be between 0 and 100'));
      return;
    }

    if (!deliveryDate) {
      setError(t('calculatorForm.deliveryDateRequired', 'Delivery date is required'));
      return;
    }

    setCalculating(true);
    try {
      const data = await deliveryPricingClient.resolve({
        shippingMethodId: parseInt(shippingMethodId),
        retailerId: retailerId ? parseInt(retailerId) : undefined,
        wholePallets: wholePalletsNumber,
        lastPalletFillPercent: fillPercent,
        deliveryDate: deliveryDate ? (deliveryDate as unknown as DateOnly) : undefined,
        baseCharge: baseCharge ? parseFloat(baseCharge) : undefined,
      });
      setResult(data);
    } catch {
      setError(t('calculatorForm.resolveFailed', 'Failed to resolve price. Check that a matching rule exists.'));
    } finally {
      setCalculating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth>
      <DialogTitle>{t('calculator', 'Price Calculator')}</DialogTitle>
      <DialogContent>
        <Grid
          container
          spacing={2}
          sx={{ mt: 1 }}>
          <Grid
            item
            xs={6}>
            {loadingOptions ? (
              <CircularProgress size={20} />
            ) : (
              <TextField
                select
                fullWidth
                required
                label={t('calculatorForm.shippingMethod', 'Shipping Method')}
                value={shippingMethodId}
                onChange={e => setShippingMethodId(e.target.value)}
              >
                <MenuItem value=''>{t('calculatorForm.selectShippingMethod', 'Select shipping method')}</MenuItem>
                {shippingMethods.map(m => (
                  <MenuItem
                    key={m.ShippingMethodId}
                    value={m.ShippingMethodId?.toString() ?? ''}>
                    {m.Name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
          <Grid
            item
            xs={6}>
            <DatePickerLocalized
              label={t('calculatorForm.deliveryDate', 'Delivery Date')}
              value={deliveryDate}
              useCalendar={false}
              minDate={dayjs(today)}
              onChange={(value) => setDeliveryDate(value ?? '')}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sx={{ pb: 0.5 }}>
            <TextField
              select
              fullWidth
              label={t('calculatorForm.retailer', 'Retailer')}
              value={retailerId}
              onChange={e => setRetailerId(e.target.value)}
              helperText={t('calculatorForm.retailerHelp', 'Select retailer or leave empty for global')}
              FormHelperTextProps={{ sx: { ml: 0, mt: 0.75 } }}
            >
              <MenuItem value=''>{t('calculatorForm.none', 'None')}</MenuItem>
              {sortedRetailers.map(r => (
                <MenuItem
                  key={r.retailerId}
                  value={r.retailerId?.toString() ?? ''}>
                  {r.name ?? ''}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid
            item
            xs={4}>
            <TextField
              fullWidth
              type="number"
              label={t('calculatorForm.wholePallets', 'Whole Delivery Units')}
              value={wholePallets}
              onChange={e => setWholePallets(e.target.value)}
              placeholder={t('calculatorForm.wholePalletsPlaceholder', 'e.g. 4')}
              inputProps={{ min: 0, step: 1 }}
            />
          </Grid>
          <Grid
            item
            xs={4}>
            <TextField
              fullWidth
              type="number"
              label={t('calculatorForm.lastPalletFillPercent', 'Last Delivery Unit Fill %')}
              value={lastPalletFillPercent}
              onChange={e => setLastPalletFillPercent(e.target.value)}
              placeholder={t('calculatorForm.lastPalletFillPercentPlaceholder', 'e.g. 45')}
              inputProps={{ min: 0, max: 100, step: 1 }}
            />
          </Grid>
          <Grid
            item
            xs={4}>
            <TextField
              fullWidth
              type="number"
              label={t('calculatorForm.baseCharge', 'Base Charge')}
              value={baseCharge}
              onChange={e => setBaseCharge(e.target.value)}
              helperText={t('calculatorForm.baseChargeHelp', 'For Discount % rate type')}
              FormHelperTextProps={{ sx: { ml: 0, mt: 0.75 } }}
              placeholder={t('calculatorForm.baseChargePlaceholder', 'e.g. 66.85')}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>

          {error && (
            <Grid
              item
              xs={12}>
              <Typography
                color="error">{error}</Typography>
            </Grid>
          )}

          {result && (
            <Grid
              item
              xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box
                sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography gutterBottom>
                  {result.resolved
                    ? t('calculatorResult.resolved', 'Price Resolved')
                    : t('calculatorResult.noMatch', 'No Matching Rule')}
                </Typography>
                {result.resolved && (
                  <Grid
                    container
                    spacing={1}>
                    <Grid
                      item
                      xs={6}>
                      <Typography
                        color="text.secondary">{t('calculatorResult.totalCharge', 'Total Charge')}</Typography>
                      <Typography>{(result.totalCharge ?? 0).toFixed(2)}</Typography>
                    </Grid>
                    <Grid
                      item
                      xs={6}>
                      <Typography
                        color="text.secondary">{t('calculatorResult.perUnitRate', 'Per Unit Rate')}</Typography>
                      <Typography>{(result.perUnitRate ?? 0).toFixed(4)}</Typography>
                    </Grid>
                    <Grid
                      item
                      xs={6}>
                      <Typography
                        color="text.secondary">{t('calculatorResult.wholePallets', 'Whole Delivery Units')}</Typography>
                      <Typography>{result.wholePallets}</Typography>
                    </Grid>
                    <Grid
                      item
                      xs={6}>
                      <Typography
                        color="text.secondary">{t('calculatorResult.partialPalletCharge', 'Partial Delivery Unit Charge')}</Typography>
                      <Typography>{(result.partialPalletCharge ?? 0).toFixed(2)}</Typography>
                    </Grid>
                    {result.modelName && (
                      <Grid
                        item
                        xs={6}>
                        <Typography
                          color="text.secondary">{t('calculatorResult.model', 'Model')}</Typography>
                        <Typography>{result.modelName}</Typography>
                      </Grid>
                    )}
                    {result.scope && (
                      <Grid
                        item
                        xs={6}>
                        <Typography
                          color="text.secondary">{t('calculatorResult.scope', 'Scope')}</Typography>
                        <Typography>{result.scope}</Typography>
                      </Grid>
                    )}
                  </Grid>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('close', 'Close')}</Button>
        <Button
          onClick={handleCalculate}
          disabled={calculating}>
          {calculating ? t('calculatorCalculating', 'Calculating...') : t('calculatorAction', 'Calculate')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PriceCalculator;
